// Markeringen aanbrengen op een schermafbeelding vóór ze als bijlage meegaat
// (Optimise: feedbackvenster en gespreksdraad). Opent meteen na een opname, en
// via "Markeren" op elke afbeelding in de bijlagenlijst.
//
// Het beeld wordt passend verkleind getoond; de vormen worden in BEELDPIXELS
// bewaard (zie schermMarkering.js) en bij Bewaren op volle grootte in een nieuw
// PNG-bestand gebrand. Overslaan of sluiten laat het oorspronkelijke bestand
// ongemoeid.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Square, MoveUpRight, Highlighter, Type, Undo2, Trash2, Check, X } from 'lucide-react';
import { MARKEER_KLEUREN, MARKEER_GEREEDSCHAP, tekenVormen, laadAfbeelding, maakBestandMetMarkering, isLeeg } from './schermMarkering';

const ICOON = { kader: Square, pijl: MoveUpRight, stift: Highlighter, tekst: Type };

export default function SchermMarkeerVenster({ file, onKlaar, onSluiten }) {
  const [img, setImg] = useState(null);
  const [fout, setFout] = useState(null);
  const [gereedschap, setGereedschap] = useState('kader');
  const [kleur, setKleur] = useState(MARKEER_KLEUREN[0].hex);
  const [vormen, setVormen] = useState([]);
  const [bezigVorm, setBezigVorm] = useState(null);   // de vorm die nu gesleept wordt
  const [tekstInvoer, setTekstInvoer] = useState(null); // { x, y } in beeldpixels
  const [tekst, setTekst] = useState('');
  const [bewaren, setBewaren] = useState(false);
  const [maat, setMaat] = useState({ b: 0, h: 0, schaal: 1 });
  const vlakRef = useRef(null);
  const canvasRef = useRef(null);
  const tekstRef = useRef(null);

  useEffect(() => {
    let weg = false;
    laadAfbeelding(file).then((i) => { if (!weg) setImg(i); }).catch((e) => { if (!weg) setFout(e.message); });
    return () => { weg = true; };
  }, [file]);

  // Passend verkleinen binnen het beschikbare vlak; opnieuw bij venstergrootte.
  const meet = useCallback(() => {
    if (!img || !vlakRef.current) return;
    const r = vlakRef.current.getBoundingClientRect();
    const beschikbaarB = Math.max(200, r.width);
    const beschikbaarH = Math.max(200, window.innerHeight - r.top - 24);
    const s = Math.min(1, beschikbaarB / img.naturalWidth, beschikbaarH / img.naturalHeight);
    setMaat({ b: Math.round(img.naturalWidth * s), h: Math.round(img.naturalHeight * s), schaal: s });
  }, [img]);
  useEffect(() => { meet(); window.addEventListener('resize', meet); return () => window.removeEventListener('resize', meet); }, [meet]);

  // Tekenen: beeld + bewaarde vormen + de vorm die nu gesleept wordt.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !img || !maat.b) return;
    c.width = maat.b; c.height = maat.h;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, maat.b, maat.h);
    tekenVormen(ctx, bezigVorm ? [...vormen, bezigVorm] : vormen, { schaal: maat.schaal, breedte: img.naturalWidth });
  }, [img, maat, vormen, bezigVorm]);

  useEffect(() => { if (tekstInvoer && tekstRef.current) tekstRef.current.focus(); }, [tekstInvoer]);

  // Muis-/aanraakpositie → beeldpixels.
  const beeldPunt = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / maat.schaal, y: (e.clientY - r.top) / maat.schaal };
  };

  // De vorm die gesleept wordt staat óók in een ref: pointermove/pointerup
  // kunnen binnenkomen vóór React de state van pointerdown verwerkt heeft
  // (snelle beweging, pen, geautomatiseerde invoer) en zouden de sleep dan
  // missen. De state dient enkel om te tekenen.
  const bezigRef = useRef(null);
  const zetBezig = (v) => { bezigRef.current = v; setBezigVorm(v); };
  const start = (e) => {
    if (!img || tekstInvoer) return;
    e.preventDefault();
    const p = beeldPunt(e);
    if (gereedschap === 'tekst') { setTekstInvoer(p); setTekst(''); return; }
    canvasRef.current.setPointerCapture?.(e.pointerId);
    if (gereedschap === 'stift') zetBezig({ soort: 'stift', kleur, punten: [p] });
    else zetBezig({ soort: gereedschap, kleur, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const beweeg = (e) => {
    const v = bezigRef.current;
    if (!v) return;
    const p = beeldPunt(e);
    zetBezig(v.soort === 'stift' ? { ...v, punten: [...v.punten, p] } : { ...v, x2: p.x, y2: p.y });
  };
  const stop = (e) => {
    let v = bezigRef.current;
    if (!v) return;
    // Het eindpunt van pointerup zelf meenemen: bij een sleep zonder tussenliggende
    // move-events is dat het enige punt dat de vorm haar maat geeft.
    if (e && e.clientX != null && v.soort !== 'stift') { const p = beeldPunt(e); v = { ...v, x2: p.x, y2: p.y }; }
    if (!isLeeg(v)) setVormen((l) => [...l, v]);
    zetBezig(null);
  };
  // Wordt door Enter én door blur aangeroepen: na Enter verdwijnt het veld en
  // vuurt blur nog een keer, dan is er niets meer te bevestigen.
  const bevestigTekst = () => {
    if (!tekstInvoer) return;
    const v = { soort: 'tekst', kleur, x: tekstInvoer.x, y: tekstInvoer.y, tekst: tekst.trim() };
    if (!isLeeg(v)) setVormen((l) => [...l, v]);
    setTekstInvoer(null); setTekst('');
  };

  const bewaar = async () => {
    setBewaren(true); setFout(null);
    try { onKlaar(await maakBestandMetMarkering(file, vormen)); }
    catch (e) { setFout(e.message || 'Bewaren mislukt.'); setBewaren(false); }
  };

  const knop = 'flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg disabled:opacity-40';
  const cursor = gereedschap === 'tekst' ? 'cursor-text' : 'cursor-crosshair';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/80 z-[9700] flex flex-col p-3 sm:p-5" onClick={onSluiten} data-dropdown-panel>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-6xl mx-auto flex flex-col min-h-0 flex-1" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-700/70">
          <h3 className="text-sm font-bold text-slate-100 mr-2">Markeer wat je bedoelt</h3>
          <div className="flex items-center gap-1 bg-slate-800/70 rounded-lg p-1">
            {MARKEER_GEREEDSCHAP.map((g) => { const I = ICOON[g.id]; return (
              <button key={g.id} type="button" onClick={() => setGereedschap(g.id)} title={g.uitleg}
                className={`${knop} ${gereedschap === g.id ? 'bg-lime-500 text-slate-900 font-semibold' : 'text-slate-200 hover:bg-slate-700'}`}>
                <I size={13} />{g.label}
              </button>); })}
          </div>
          <div className="flex items-center gap-1.5 ml-1" title="Kleur">
            {MARKEER_KLEUREN.map((k) => (
              <button key={k.id} type="button" onClick={() => setKleur(k.hex)} aria-label={k.label} title={k.label}
                className={`w-5 h-5 rounded-full border-2 ${kleur === k.hex ? 'border-white scale-110' : 'border-transparent'}`} style={{ background: k.hex }} />
            ))}
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <button type="button" onClick={() => setVormen((l) => l.slice(0, -1))} disabled={!vormen.length} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200`} title="Laatste markering ongedaan maken"><Undo2 size={13} />Ongedaan</button>
            <button type="button" onClick={() => setVormen([])} disabled={!vormen.length} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200`} title="Alle markeringen wissen"><Trash2 size={13} />Wissen</button>
            <button type="button" onClick={() => onKlaar(null)} className={`${knop} bg-slate-700 hover:bg-slate-600 text-slate-200`} title="Zonder markeringen toevoegen">Overslaan</button>
            <button type="button" onClick={bewaar} disabled={bewaren || !img} className={`${knop} bg-lime-500 hover:bg-lime-400 text-slate-900 font-semibold`}><Check size={13} />{bewaren ? 'Bezig…' : vormen.length ? 'Bewaren met markeringen' : 'Toevoegen'}</button>
            <button type="button" onClick={onSluiten} aria-label="Sluiten" className="p-1.5 text-slate-400 hover:text-slate-200 rounded-lg hover:bg-slate-700/50"><X size={16} /></button>
          </div>
        </div>
        {fout && <div className="mx-4 mt-3 bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm">{fout}</div>}
        <div ref={vlakRef} className="flex-1 min-h-0 overflow-auto p-4 flex items-start justify-center">
          {!img && !fout && <div className="text-sm text-slate-400 py-10">Afbeelding laden…</div>}
          {img && (
            <div className="relative" style={{ width: maat.b, height: maat.h }}>
              <canvas ref={canvasRef} className={`block rounded-lg shadow-lg touch-none ${cursor}`} style={{ width: maat.b, height: maat.h }}
                onPointerDown={start} onPointerMove={beweeg} onPointerUp={stop} onPointerCancel={stop} onPointerLeave={stop} />
              {tekstInvoer && (
                <input ref={tekstRef} value={tekst} onChange={(e) => setTekst(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') bevestigTekst(); if (e.key === 'Escape') { setTekstInvoer(null); setTekst(''); } }}
                  onBlur={bevestigTekst} placeholder="Typ en druk Enter"
                  className="absolute px-1.5 py-0.5 text-sm font-bold bg-white/90 text-slate-900 border-2 rounded"
                  style={{ left: tekstInvoer.x * maat.schaal, top: tekstInvoer.y * maat.schaal, borderColor: kleur, minWidth: 140 }} />
              )}
            </div>
          )}
        </div>
        <div className="px-4 pb-3 text-[11px] text-slate-500">Sleep om een kader of pijl te trekken, teken met de stift, of klik voor tekst. Bewaren brandt de markeringen in de afbeelding.</div>
      </div>
    </div>,
    document.body
  );
}
